import { useCompletionReadSync } from "../neucleos/threadCompletionReads";
import { AppearancePrefsSync } from "../neucleos/AppearancePrefsSync";
import { lightIntensityFactor, resolveTypeScale } from "@neucleos/theme";
import { DoorHoverLight } from "../components/DoorHoverLight";
import { openCommandPalette } from "../commandPaletteBus";
import { dispatchDeskCommand } from "../deskCommandBus";
import { toggleCleoWide } from "../components/cleoWide";
import { useDesktopMenuCommands } from "../desktopMenuCommands";
import { type ServerLifecycleWelcomePayload } from "@cleo/contracts";
import {
  scopedProjectKey,
  scopeProjectRef,
  scopeThreadRef,
} from "@cleo/client-runtime/environment";
import { squashAtomCommandFailure } from "@cleo/client-runtime/state/runtime";
import { Outlet, createRootRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useEffectEvent, useRef, useState } from "react";

import { APP_BASE_NAME, APP_DISPLAY_NAME, APP_STAGE_LABEL } from "../branding";
import { resolveServerBackedAppDisplayName } from "../branding.logic";
import { AppSidebarLayout } from "../components/AppSidebarLayout";
import { NotYourCleoStrip } from "../components/NotYourCleoStrip";
import { NeucleosWorldFrame } from "../components/NeucleosWorldFrame";
import { NeucleosRoomFrame } from "../components/NeucleosRoomFrame";
import { CommandPalette } from "../components/CommandPalette";
import { WorldViewProvider } from "../neucleos/worldView";
import { WorldViewPendingIndicator } from "../components/WorldViewStateFace";
import { ConfirmDialogHost } from "../components/ConfirmDialogHost";
import { InvitationLinkOnceHost } from "../components/InvitationLinkOnceHost";
import { ConnectOnboardingDialog } from "../components/cloud/ConnectOnboardingDialog";
import { RelayClientInstallDialog } from "../components/cloud/RelayClientInstallDialog";
import { SshPasswordPromptDialog } from "../components/desktop/SshPasswordPromptDialog";
import { ProviderUpdateLaunchNotification } from "../components/ProviderUpdateLaunchNotification";
import { SlowRpcRequestToastCoordinator } from "../components/SlowRpcRequestToastCoordinator";
import { SessionNotificationCoordinator } from "../notifications/SessionNotificationCoordinator";
import { RootErrorPage } from "../components/RootErrorPage";
import { ThemeEditorHost } from "../components/settings/ThemeEditorHost";
import {
  AnchoredToastProvider,
  stackedThreadToast,
  ToastProvider,
  toastManager,
} from "../components/ui/toast";
import { resolveAndPersistPreferredEditor } from "../editorPreferences";
import { applyAppearanceFontVariables } from "~/appearanceFonts";
import { useClientSettings } from "../hooks/useSettings";
import { repaintSectionAccents, setSectionToneDown } from "../themePalette";
import {
  deriveLogicalProjectKeyFromSettings,
  derivePhysicalProjectKeyFromPath,
  selectProjectGroupingSettings,
} from "../logicalProject";
import { useUiStateStore } from "../uiStateStore";
import { syncBrowserChromeTheme, useTheme } from "../hooks/useTheme";
import { configureClientTracing } from "../observability/clientTracing";
import { resolveInitialServerAuthGateState } from "../environments/primary";
import { hasHostedPairingRequest, isHostedStaticApp } from "../hostedPairing";
import { isWebHost } from "../webHost";
import { shellOpensFor } from "../authGate";
import { shellEnvironment } from "../state/shell";
import { useAtomValue } from "@effect/atom-react";
import { useAtomCommand } from "../state/use-atom-command";
import { useEnvironments, usePrimaryEnvironment } from "../state/environments";
import {
  primaryServerConfigAtom,
  primaryServerConfigEventAtom,
  primaryServerWelcomeAtom,
} from "../state/server";
import {
  readProject,
  readThreadShell,
  setActiveEnvironmentId,
  useActiveEnvironmentId,
} from "../state/entities";
import {
  createKeybindingsUpdateToastController,
  type KeybindingsUpdateToastController,
} from "../components/KeybindingsUpdateToast.logic";
import { threadBelongsToWorld, allModeActive, localModeActive } from "~/neucleos/worldSelection";
import { useNeucleosWorldStore } from "~/neucleos/worldStore";
import { useWorldsFollowServer } from "~/neucleos/useWorldsFollowServer";

export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    if (location.pathname === "/pair" && hasHostedPairingRequest(new URL(window.location.href))) {
      return {
        authGateState: {
          status: "hosted-pairing",
        } as const,
      };
    }

    // cleo-web (webHost.ts): no Mac server to ask; the shell opens, and the page's own Clerk decides who is signed in.
    // Decided before the hosted pairing page's test: hosted at its own origin, the web host is a web host first.
    if (isWebHost()) {
      return {
        authGateState: {
          status: "web-host",
        } as const,
      };
    }

    if (isHostedStaticApp(new URL(window.location.href))) {
      return {
        authGateState: {
          status: "hosted-static",
        } as const,
      };
    }

    const authGateState = await resolveInitialServerAuthGateState();
    return {
      authGateState,
    };
  },
  component: RootRouteView,
  errorComponent: RootErrorPage,
  head: () => ({
    meta: [{ name: "title", content: APP_DISPLAY_NAME }],
  }),
});

function RootRouteView() {
  useCompletionReadSync();
  const pathname = useLocation({ select: (location) => location.pathname });
  const { authGateState } = Route.useRouteContext();
  const primaryEnvironmentAuthenticated = authGateState.status === "authenticated";

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      syncBrowserChromeTheme();
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [pathname]);

  if (
    pathname === "/pair" ||
    pathname === "/connect" ||
    pathname.startsWith("/connect/") ||
    // CVA4: the Desk's isolated thumbnail capture renders a composition alone.
    pathname === "/canvas-capture"
  ) {
    return (
      <>
        <DocumentTitleSync />
        <NotYourCleoStrip />
        <DesignFloorRootAttributes />
        <Outlet />
      </>
    );
  }

  if (!shellOpensFor(authGateState.status)) {
    return (
      <>
        <DocumentTitleSync />
        <NotYourCleoStrip />
        <DesignFloorRootAttributes />
        <Outlet />
      </>
    );
  }

  const appShell = (
    <NeucleosWorldFrame>
      {/* TR1: the room's one authority is seated ABOVE the palette, so the
          palette reads the real context — the mirror is gone, not moved. */}
      <WorldViewProvider>
        <WorldViewPendingIndicator />
        <CommandPalette>
          <NeucleosRoomFrame>
            <AppSidebarLayout>
              <Outlet />
            </AppSidebarLayout>
          </NeucleosRoomFrame>
        </CommandPalette>
      </WorldViewProvider>
    </NeucleosWorldFrame>
  );

  return (
    <ToastProvider>
      <AnchoredToastProvider>
        <DocumentTitleSync />
        <NotYourCleoStrip />
        <DesignFloorRootAttributes />
        <GlassAppearanceSync />
        <ShellEffectsSync />
        <AppearancePrefsSync />
        <FontAppearanceSync />
        {primaryEnvironmentAuthenticated ? <AuthenticatedTracingBootstrap /> : null}
        <RelayClientInstallDialog />
        {/* cleo-web (webHost.ts): the Connect wizard publishes this Mac's environment; the browser host has none. */}
        {isWebHost() ? null : <ConnectOnboardingDialog />}
        <SshPasswordPromptDialog />
        <ConfirmDialogHost />
        {/* #1730: an invitation link the founder's own Approve made, shown once on this device. */}
        <InvitationLinkOnceHost />
        <SlowRpcRequestToastCoordinator />
        {primaryEnvironmentAuthenticated ? <SessionNotificationCoordinator /> : null}
        <HostedStaticEnvironmentBootstrap />
        {primaryEnvironmentAuthenticated ? <EventRouter /> : null}
        {primaryEnvironmentAuthenticated ? <ProviderUpdateLaunchNotification /> : null}
        {appShell}
        {/* Above the router: a theme draft is judged by walking the app, so the
            editor has to survive navigation away from settings. */}
        <ThemeEditorHost />
      </AnchoredToastProvider>
    </ToastProvider>
  );
}

function GlassAppearanceSync() {
  const glassOpacity = useClientSettings((settings) => settings.glassOpacity);

  useEffect(() => {
    document.documentElement.style.setProperty("--glass-opacity", `${glassOpacity}%`);
  }, [glassOpacity]);

  return null;
}

/**
 * FD26 (register FD-CAN-26): the tone-down knobs' one document seat. The
 * section painter takes the tone-down STANCE (it stays the only
 * `--ncl-section-*` writer); the rest/opaque flags ride the root dataset
 * for index.css to read. OS signals always win downward — reduced motion
 * stills the drift and forced colors keep their own opaque law
 * regardless of these settings.
 */
function ShellEffectsSync() {
  const appearanceTheme = useTheme();
  const light = useClientSettings();
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.opaqueDoors = String(light.opaqueDoors);
    root.dataset.lightBreathe = light.lightBreathe ? "on" : "off";
    root.style.setProperty("--ncl-effect-glow-share", `${light.cardGlow}%`);
    root.style.setProperty(
      "--ncl-effect-light-intensity",
      String(lightIntensityFactor(light.lightIntensity)),
    );
  }, [
    light.opaqueDoors,
    light.lightIntensity,
    light.lightBreathe,
    light.cardGlow,
    appearanceTheme.theme,
    appearanceTheme.resolvedTheme,
  ]);
  const toneDownSections = useClientSettings((settings) => settings.toneDownSections);
  const ambientMotion = useClientSettings((settings) => settings.ambientMotion);
  const shellGlass = useClientSettings((settings) => settings.shellGlass);
  // 2026-09-03 (the principal: density is a real mode, controlled from
  // Settings alone — "I don't think I'd want to clutter the top with it"):
  // the Appearance row's value is stamped on the root so the rhythm sheet
  // (index.css, :root[data-density="compact"]) can tighten the rails and
  // the deck strip from one seat.
  const density = useClientSettings((settings) => settings.density);

  useEffect(() => {
    const root = document.documentElement;
    if (density === "compact") {
      root.dataset.density = "compact";
    } else {
      delete root.dataset.density;
    }
  }, [density]);

  useEffect(() => {
    setSectionToneDown(toneDownSections);
    repaintSectionAccents();
  }, [toneDownSections]);

  useEffect(() => {
    const root = document.documentElement;
    if (ambientMotion) {
      delete root.dataset.ambientMotion;
    } else {
      root.dataset.ambientMotion = "off";
    }
    if (shellGlass === "opaque") {
      root.dataset.shellGlass = "opaque";
    } else {
      delete root.dataset.shellGlass;
    }
  }, [ambientMotion, shellGlass]);

  return <DoorHoverLight enabled={light.hoverLight && light.lightStudy !== "none"} />;
}

function FontAppearanceSync() {
  const fontFamilySans = useClientSettings((settings) => settings.fontFamilySans);
  const fontFamilyCode = useClientSettings((settings) => settings.fontFamilyCode);
  const fontFamilyComposer = useClientSettings((settings) => settings.fontFamilyComposer);
  const fontSizeInterface = useClientSettings((settings) => settings.fontSizeInterface);
  const fontSizePrompt = useClientSettings((settings) => settings.fontSizePrompt);
  const fontSizeCode = useClientSettings((settings) => settings.fontSizeCode);
  const fontSmoothing = useClientSettings((settings) => settings.fontSmoothing);

  useEffect(() => {
    applyAppearanceFontVariables(document.documentElement, {
      sans: fontFamilySans,
      code: fontFamilyCode,
      composer: fontFamilyComposer,
      sizeInterface: fontSizeInterface,
      sizePrompt: fontSizePrompt,
      sizeCode: fontSizeCode,
      smoothing: fontSmoothing,
    });
  }, [
    fontFamilyCode,
    fontFamilyComposer,
    fontFamilySans,
    fontSizeCode,
    fontSizeInterface,
    fontSizePrompt,
    fontSmoothing,
  ]);

  return null;
}

/**
 * Design floors (GOALS/GOALS-DESIGN.html). HIG1: the contract's type ladder
 * is re-applied per density so --ncl-type-* is one resolver, never a second
 * source beside the stylesheet's fallback. HIG3: the person's contrast choice
 * (or the OS Increase Contrast setting under "system") stamps the root as
 * data-contrast="high". HIG8: the Mac menu bar's commands land on the desk's
 * existing doors — the palette bus and the desk command bus.
 */
function DesignFloorRootAttributes() {
  const density = useClientSettings((settings) => settings.density);
  const contrast = useClientSettings((settings) => settings.contrast);

  useEffect(() => {
    const root = document.documentElement;
    const variables = resolveTypeScale({
      platform: "macos",
      density: density === "compact" ? "compact" : "comfortable",
    });
    for (const [name, value] of Object.entries(variables)) {
      root.style.setProperty(name, value);
    }
  }, [density]);

  useEffect(() => {
    const root = document.documentElement;
    const media = window.matchMedia("(prefers-contrast: more)");
    const apply = () => {
      if (contrast === "high" || (contrast === "system" && media.matches)) {
        root.dataset.contrast = "high";
      } else if (contrast === "standard") {
        root.dataset.contrast = "standard";
      } else {
        delete root.dataset.contrast;
      }
    };
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [contrast]);

  useDesktopMenuCommands({
    "new-room": () => dispatchDeskCommand("new-room"),
    "new-thread": () => openCommandPalette({ open: "new-thread-in" }),
    "go-to-file": () => openCommandPalette({ open: "files" }),
    "search-commands": () => openCommandPalette(),
    "toggle-room-rail": () => dispatchDeskCommand("toggle-room-rail"),
    "toggle-context-rail": () => dispatchDeskCommand("toggle-context-rail"),
    "toggle-cleo-wide": toggleCleoWide,
  });

  return null;
}

function DocumentTitleSync() {
  const primaryServerVersion =
    useAtomValue(primaryServerConfigAtom)?.environment.serverVersion ?? null;
  const appTitle = resolveServerBackedAppDisplayName({
    baseName: APP_BASE_NAME,
    fallbackDisplayName: APP_DISPLAY_NAME,
    fallbackStageLabel: APP_STAGE_LABEL,
    primaryServerVersion,
  });
  // HIG8 (GOALS/GOALS-DESIGN.html#hig8): the window title names the content —
  // the World in front — instead of only the wordmark. The desktop main
  // process applies it through page-title-updated (DesktopWindow.ts).
  const activeWorldName = useNeucleosWorldStore(
    (state) => state.worlds.find((world) => world.worldKey === state.activeWorldKey)?.name ?? null,
  );
  const title = activeWorldName ? `${activeWorldName} · ${appTitle}` : appTitle;

  useEffect(() => {
    document.title = title;
  }, [title]);

  return null;
}

function HostedStaticEnvironmentBootstrap() {
  const { environments } = useEnvironments();
  const activeEnvironmentId = useActiveEnvironmentId();

  useEffect(() => {
    if (
      environments.some(
        (environment) => environment.entry.target._tag === "PrimaryConnectionTarget",
      )
    ) {
      return;
    }

    if (activeEnvironmentId) {
      return;
    }

    const firstSavedEnvironment = environments[0];
    if (!firstSavedEnvironment) {
      return;
    }

    setActiveEnvironmentId(firstSavedEnvironment.environmentId);
  }, [activeEnvironmentId, environments]);

  return null;
}

function AuthenticatedTracingBootstrap() {
  useEffect(() => {
    void configureClientTracing();
  }, []);

  return null;
}

function EventRouter() {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (loc) => loc.pathname });
  const projectGroupingSettings = useClientSettings(selectProjectGroupingSettings);
  const primaryEnvironment = usePrimaryEnvironment();
  const openInEditor = useAtomCommand(shellEnvironment.openInEditor, {
    reportFailure: false,
  });
  const serverConfig = useAtomValue(primaryServerConfigAtom);
  const serverConfigEvent = useAtomValue(primaryServerConfigEventAtom);
  const serverWelcome = useAtomValue(primaryServerWelcomeAtom);
  useWorldsFollowServer(serverConfigEvent);
  const readPathname = useEffectEvent(() => pathname);
  const handledBootstrapThreadIdRef = useRef<string | null>(null);
  const handledConfigEventRef = useRef(serverConfigEvent);
  const [keybindingsToastController] = useState<KeybindingsUpdateToastController>(() =>
    createKeybindingsUpdateToastController({}),
  );

  const handleWelcome = useEffectEvent((payload: ServerLifecycleWelcomePayload | null) => {
    if (!payload) return;

    setActiveEnvironmentId(payload.environment.environmentId);
    void (async () => {
      if (!payload.bootstrapProjectId || !payload.bootstrapThreadId) {
        return;
      }
      const bootstrapProject = readProject(
        scopeProjectRef(payload.environment.environmentId, payload.bootstrapProjectId),
      );
      const bootstrapProjectKey =
        (bootstrapProject
          ? deriveLogicalProjectKeyFromSettings(bootstrapProject, projectGroupingSettings)
          : null) ??
        (serverConfig?.cwd
          ? derivePhysicalProjectKeyFromPath(payload.environment.environmentId, serverConfig.cwd)
          : null) ??
        scopedProjectKey(
          scopeProjectRef(payload.environment.environmentId, payload.bootstrapProjectId),
        );
      useUiStateStore.getState().setProjectExpanded(bootstrapProjectKey, true);

      if (readPathname() !== "/") {
        return;
      }
      const worldState = useNeucleosWorldStore.getState();
      if (!worldState.loaded) {
        return;
      }
      // A shell not yet synced resolves to null and stays gated in
      // multi-World mode; the thread remains reachable from its own tab.
      const bootstrapThreadShell = readThreadShell(
        scopeThreadRef(payload.environment.environmentId, payload.bootstrapThreadId),
      );
      if (
        !threadBelongsToWorld(bootstrapThreadShell?.worldKey, {
          activeWorldKey: worldState.activeWorldKey,
          local: localModeActive({
            signedIn: worldState.signedIn,
            worldCount: worldState.worlds.length,
            selection: worldState.selection,
          }),
          all: allModeActive(worldState.selection),
        })
      ) {
        return;
      }
      if (handledBootstrapThreadIdRef.current === payload.bootstrapThreadId) {
        return;
      }
      await navigate({
        to: "/$environmentId/$threadId",
        params: {
          environmentId: payload.environment.environmentId,
          threadId: payload.bootstrapThreadId,
        },
        replace: true,
      });
      handledBootstrapThreadIdRef.current = payload.bootstrapThreadId;
    })().catch(() => undefined);
  });

  const handleServerConfigUpdated = useEffectEvent(() => {
    const decision = keybindingsToastController.handle(serverConfigEvent);
    if (!decision) {
      return;
    }

    if (decision._tag === "Success") {
      toastManager.add({
        type: "success",
        title: "Keybindings updated",
        description: "Keybindings configuration reloaded successfully.",
      });
      return;
    }

    toastManager.add(
      stackedThreadToast({
        type: "warning",
        title: "Invalid keybindings configuration",
        description: decision.message,
        actionVariant: "outline",
        actionProps: {
          children: "Open keybindings.json",
          onClick: () => {
            if (!serverConfig || !primaryEnvironment) {
              return;
            }

            const editor = resolveAndPersistPreferredEditor(serverConfig.availableEditors);
            if (!editor) {
              return;
            }
            void (async () => {
              const result = await openInEditor({
                environmentId: primaryEnvironment.environmentId,
                input: {
                  cwd: serverConfig.keybindingsConfigPath,
                  editor,
                },
              });
              if (result._tag === "Success") {
                return;
              }
              const error = squashAtomCommandFailure(result);
              toastManager.add(
                stackedThreadToast({
                  type: "error",
                  title: "Unable to open keybindings file",
                  description:
                    error instanceof Error ? error.message : "Unknown error opening file.",
                }),
              );
            })();
          },
        },
      }),
    );
  });

  useEffect(() => {
    if (!serverConfig) {
      return;
    }

    setActiveEnvironmentId(serverConfig.environment.environmentId);
  }, [serverConfig]);

  useEffect(() => {
    handleWelcome(serverWelcome);
  }, [serverWelcome]);

  useEffect(() => {
    if (serverConfigEvent === null || handledConfigEventRef.current === serverConfigEvent) {
      return;
    }
    handledConfigEventRef.current = serverConfigEvent;
    handleServerConfigUpdated();
  }, [serverConfigEvent]);

  return null;
}
